import { axios } from "../../config/axios";

export const isMerchantSegment = (obj) => {
  return typeof obj.id === 'number'
    && typeof obj.name === 'string';
}

export const isMiscSegment = (obj) => {
  return obj.id === 'misc'
    && typeof obj.name === 'string';
}

export const isPinCodeSegment = (obj) => {
  return obj.id === 'pin_code_status'
    && typeof obj.name === 'string';
}

export const isSegmentChild = (obj) => {
  return typeof obj.id === 'number'
    && typeof obj.values === 'object';
}

export const isMerchantTag = (obj) => {
  return typeof obj.id === 'string'
    && typeof obj.name === 'string'
    && typeof obj.context === 'string';
}

export const isTagChild = (obj) => {
  return typeof obj.id === 'number' &&
    typeof obj.tag_id === 'number' &&
    typeof obj.tag === 'string'
}

export const groupsAPI = {
  /**
   * Retrieve all groups (Segments + tags)
   * @param {Array} merchantSegmentIds
   * @param {Array} merchantTagIds
   * @returns {Object} { groups_with_selected: []}
   */
  getGroups(merchantSegmentIds, merchantTagIds) {
    const params = {
      merchant_segment_ids: merchantSegmentIds,
      merchant_tag_ids: merchantTagIds,
      legacy: true
    };
    return axios.get('/api/v3/groups/', { params })
                .then(resp => resp.data)
  },
  /**
   * Returns all segments and taggings for a given group transformed to shared interface
   * @param {*} group
   * @param {number} page
   * @param {string} search
   * @param {number} per_page
   * @return Promise.<Object.<{
   *   children: Array.<{ id:string | number, label:string }>
   * }>>
   */
  async getGroupChildren(group, page, search, per_page) {
    if(isMerchantSegment(group)) {
      const result = await groupsAPI.getMerchantSegments(group.id, page, search, per_page);

      return {
        children: result['merchant_segments'].map((segment) => ({
          label: segment.title || segment.name,
          ...segment
        })),
        ...result
      };
    } else if(isMiscSegment(group)) {
      const result = await groupsAPI.getMiscSegments(search);

      return {
        children: result['merchant_segments'].map((segment) => ({
          label: segment.title || segment.name,
          ...segment
        })),
        ...result
      };
    } else if(isPinCodeSegment(group)) {
      const result = await groupsAPI.getPinCodeStatusSegments(search);

      return {
        children: result['merchant_segments'].map((segment) => ({
          label: segment.title || segment.name,
          ...segment
        })),
        ...result
      };
    } else if(isMerchantTag(group)) {
      const result = await groupsAPI.getMerchantTaggings(group.context, page, search, per_page);

      return {
        children: result['taggings_count'].map((tagging) => ({
          label: tagging.name,
          ...tagging
        })),
        ...result
      };
    }
  },

  /**
   * Returns all merchant segments
   * @param {string} search
   * @returns Promise.<Array.<Object>>
   */
  async getMiscSegments(search) {
    const resp = await axios.get('/api/v3/groups/merchant_segments', {
      params: { misc: true, search }
    });

    return resp.data;
  },

  /**
   * Returns all pin code status segments
   * @returns Promise.<Array.<Object>>
   */
     async getPinCodeStatusSegments(search) {
      const resp = await axios.get('/api/v3/groups/merchant_segments', {
        params: {segment_id: 'pin_code_status', search }
      });

      return resp.data;
    },

  /**
   * Returns all merchant segments for a given group
   * @param {number} segment_id
   * @param {number} page
   * @param {string} search
   * @param {number} per_page
   * @returns Promise.<Array.<Object>>
   */
  async getMerchantSegments(segment_id, page, search, per_page) {
    const params = {
      segment_id,
      page,
      search
    }
    if(per_page) {
      params.per_page = per_page;
    }
    const resp = await axios.get('/api/v3/groups/merchant_segments', {
      params
    });

    return resp.data;
  },

  /**
   * Returns all merchant taggings for a given context
   * @param {string} context
   * @param {number} page
   * @param {string} search
   * @param {number} per_page
   * @returns Promise.<Array.<Object>>
   */
   async getMerchantTaggings(context, page, search, per_page) {
    const params = {
      context,
      page,
      search
    }
    if(per_page) {
      params.per_page = per_page;
    }
    const resp = await axios.get('/api/v3/groups/merchant_taggings', {
      params
    });

    return resp.data;
  },

  /**
   * Returns aggregated count based on group type
   * @param {Object} group
   * @returns Promise.<Object>
   */
  async count(group, campaign_id = null) {
    if(isSegmentChild(group)) {
      return await groupsAPI.getSegmentCount(group.id, !!campaign_id);
    } else if(isTagChild(group)) {
      return await groupsAPI.getTagCount(group.tag, group.context)
    }
  },

  /**
   * Returns aggregated count associated with segment
   * @param {number} segment_id
   * @return Promise.<Object>
   */
  async getSegmentCount(segment_id, include_push = false) {
    const resp = await axios.get(`/api/v3/groups/segment_count`, {
      params: { segment_id, include_push }
    });

    return resp.data;
  },

  /**
   * Returns aggregated count associated with segment
   * @param {string} tag
   * @param {string} context
   * @returns Promise.<Object>
   */
  async getTagCount(tag, context) {
    const resp = await axios.get(`/api/v3/groups/tag_count`, {
      params: { tag, context }
    });

    return resp.data;
  }
}
